/**
 * About HiBro.
 * Figma `9229:188779` (About card, frame `9229:188628`).
 * Feedback cards reuse `SETTINGS_CARD`. Thumb radius 24 and the 10px image gap are bare
 * Figma values (`--radius/24`, gap 10). Remove badge `9229:188439` sits on the
 * thumb at left 60 / top -4, box 24×24.605 (viewBox is 24×25, so the extra
 * 0.395px is clipped). Log block `9229:188207` starts with the switch on;
 * caption `9229:188214` is gap/8 × gap/12, 13/20, Color/text/tertiary.
 * The text-field underline under the description sample is single-line chrome
 * and is not drawn in the 136px box.
 *
 * About header `9229:188780`: 112 tile, radius/32, Color/brand → `--app-base-brand`.
 * Mark is `about-logo.svg` (85.3125, `9229:188782`) plus bare `pl-[2.188px]`.
 * Title2/Medium is 22px in a 26px box; Subheadline is 15px in an 18px box
 * (lineHeight 100 lays out as normal). Gaps are margin/12 then margin/4.
 * Title keeps margin/12 left padding from the hidden VIP slot.
 * Version value is Callout/Regular 16px. Idle and checking stay Color/text/tertiary.
 * An offer (`9229:189159`) keeps the running version in Color/text/primary, then a
 * New pill (Color/text/success on Color/success/alert, padding 10×1, 11/16,
 * radius max, gap 6). Downloading (`9229:189917`) is Color/text/tertiary 14/22
 * plus `i-sending1` at 24px. Ready (`9229:189532`) is only the orange pill
 * (Color/text/orange on Color/warning/alert); the row has no chevron, the pill
 * calls `install()`.
 * The value is `IUpdateService.getIdentity` (`v ${version}`). Idle calls
 * `check(true)`; an offer calls `download()`, or `install()` when this runtime
 * has nothing to fetch. Website, agreement, and privacy are `app.homepage`,
 * `app.termsUrl`, and `app.privacyUrl`, opened externally. A missing value
 * stays blank — the nls placeholders are not a fallback.
 */
import type { ChannelClient } from '@hibro/platform-core';
import type {
  IProductConfigService,
  IUpdateService,
  UpdateIdentity,
  UpdateInfo,
  UpdatePhase,
  UpdateState,
} from '@hibro/shared';
import { canRequestUpdateCheck, UPDATE_CHANGED_EVENT } from '@hibro/shared';
import {
  SETTINGS_CARD,
  SettingsGlyph,
  SettingsNavRow,
  SettingsPane,
  toast,
} from '@hibro/ui-client';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { settingsWindowLogError, settingsWindowLogWarn } from './log';

const ABOUT_LOGO = new URL('./assets/about-logo.svg', import.meta.url).href;

/** Figma Role group: padding/10 × padding/1, Font/Size/11, Font/LineH/16, radius max. */
const UPDATE_PILL =
  'inline-flex items-center justify-center rounded-full px-[10px] py-px text-[11px] leading-4';

const ABOUT_LINK_KEYS = {
  homepage: 'app.homepage',
  terms: 'app.termsUrl',
  privacy: 'app.privacyUrl',
} as const;

export function AboutPage({
  t,
  onLogoTap,
  updateService,
  client,
  productConfig,
  openExternal,
}: {
  t: (key: string, args?: Record<string, unknown>) => string;
  /** Five quick taps unlock developer options. The mark stays a plain tile. */
  onLogoTap?: () => void;
  updateService?: IUpdateService;
  client?: ChannelClient;
  productConfig?: IProductConfigService;
  /** Opens http(s) in the system browser. Web prefers the gesture-preserving bridge. */
  openExternal?: (url: string) => void;
}): ReactNode {
  const [identity, setIdentity] = useState<UpdateIdentity | null>(null);
  const [updateState, setUpdateState] = useState<UpdateState>({
    status: 'idle',
  });
  const [links, setLinks] = useState<{
    homepage?: string;
    terms?: string;
    privacy?: string;
  }>({});
  const pendingManual = useRef(false);
  const sawChecking = useRef(false);
  /**
   * A download or install this row started. Background failures stay in the log.
   * The latch stays up through `downloading`: `download()` can resolve while the
   * driver reports the failure later, on the sink.
   */
  const pendingApply = useRef(false);
  const applyVerb = useRef<'download' | 'install' | null>(null);

  useEffect(() => {
    if (!updateService) {
      settingsWindowLogWarn('settings about update service missing');
      return;
    }
    if (!client) {
      settingsWindowLogWarn('settings about update client missing');
    }
    let active = true;
    void updateService
      .getIdentity()
      .then((next) => {
        if (active) {
          setIdentity(next);
        }
      })
      .catch((err: unknown) => {
        settingsWindowLogError('settings about version load failed', {
          reason: failureReason(err),
        });
      });
    return () => {
      active = false;
    };
  }, [updateService, client]);

  useEffect(() => {
    if (!updateService || !client) {
      return;
    }
    let disposed = false;
    let sawBroadcast = false;
    const dispose = client.listen(UPDATE_CHANGED_EVENT, (data) => {
      sawBroadcast = true;
      setUpdateState(data as UpdateState);
    });
    void updateService
      .getState()
      .then((current) => {
        if (!disposed && !sawBroadcast) {
          setUpdateState(current);
        }
      })
      .catch((err: unknown) => {
        settingsWindowLogError('settings about update state load failed', {
          reason: failureReason(err),
        });
      });
    return () => {
      disposed = true;
      dispose();
      void updateService.cancelCheck();
    };
  }, [updateService, client]);

  useEffect(() => {
    if (!productConfig) {
      settingsWindowLogWarn('settings about product config missing');
      return;
    }
    let active = true;
    void Promise.all([
      productConfig.getValue(ABOUT_LINK_KEYS.homepage),
      productConfig.getValue(ABOUT_LINK_KEYS.terms),
      productConfig.getValue(ABOUT_LINK_KEYS.privacy),
    ])
      .then(([homepage, terms, privacy]) => {
        if (!active) {
          return;
        }
        setLinks({
          homepage: readAboutUrl(ABOUT_LINK_KEYS.homepage, homepage),
          terms: readAboutUrl(ABOUT_LINK_KEYS.terms, terms),
          privacy: readAboutUrl(ABOUT_LINK_KEYS.privacy, privacy),
        });
      })
      .catch((err: unknown) => {
        settingsWindowLogError('settings about links load failed', {
          reason: failureReason(err),
        });
      });
    return () => {
      active = false;
    };
  }, [productConfig]);

  useEffect(() => {
    if (!pendingManual.current) {
      return;
    }
    if (updateState.status === 'checking') {
      sawChecking.current = true;
      return;
    }
    // A declined press never enters `checking`. Waiting for that keeps a
    // later background broadcast from reading as this click's result.
    if (!sawChecking.current) {
      return;
    }
    pendingManual.current = false;
    sawChecking.current = false;
    if (updateState.status === 'idle') {
      toast(t('settings.update.upToDate'));
      return;
    }
    if (updateState.status === 'error' && updateState.manual) {
      toast(
        t(updateErrorKey(updateState.phase), { message: updateState.message }),
      );
    }
  }, [updateState, t]);

  const finishApply = useCallback(
    (next: UpdateState): void => {
      if (!pendingApply.current) {
        return;
      }
      if (next.status === 'error') {
        const phase = next.phase;
        pendingApply.current = false;
        applyVerb.current = null;
        // A check failure belongs to the manual-check effect, and only when
        // that press is still open. Dropping the latch here keeps a later
        // download error from being reported as this click.
        if (phase === 'download' || phase === 'install') {
          toast(t(updateErrorKey(phase), { message: next.message }));
        }
        return;
      }
      // Still fetching. A sink failure can land after this promise resolves.
      if (applyVerb.current === 'download' && next.status === 'downloading') {
        return;
      }
      pendingApply.current = false;
      applyVerb.current = null;
    },
    [t],
  );

  useEffect(() => {
    finishApply(updateState);
  }, [finishApply, updateState]);

  const capabilities = identity?.capabilities;
  const canCheck = Boolean(
    capabilities &&
    updateService &&
    canRequestUpdateCheck(updateState, capabilities),
  );
  const check = () => {
    if (!updateService || !canCheck) {
      return;
    }
    pendingManual.current = true;
    sawChecking.current = false;
    void updateService.check(true).catch((err: unknown) => {
      pendingManual.current = false;
      sawChecking.current = false;
      settingsWindowLogError('settings about update check failed', {
        reason: failureReason(err),
      });
    });
  };
  const runApply = (
    verb: 'download' | 'install',
    run: () => Promise<void>,
  ): void => {
    if (!updateService) {
      return;
    }
    pendingApply.current = true;
    applyVerb.current = verb;
    void run()
      .then(() => updateService.getState())
      .then((next) => {
        finishApply(next);
      })
      .catch((err: unknown) => {
        if (!pendingApply.current) {
          return;
        }
        pendingApply.current = false;
        applyVerb.current = null;
        const reason = failureReason(err);
        settingsWindowLogError('settings about update apply failed', {
          reason,
        });
        toast(t('settings.update.error', { message: reason }));
      });
  };
  const beginOffer = (info: UpdateInfo): void => {
    if (!updateService || !capabilities) {
      return;
    }
    if (capabilities.canDownload) {
      runApply('download', () => updateService.download());
      return;
    }
    if (capabilities.canSelfInstall) {
      runApply('install', () => updateService.install());
      return;
    }
    if (info.downloadUrl) {
      open(info.downloadUrl);
      return;
    }
    toast(t('settings.update.unsupported'));
  };
  const versionClick = (): (() => void) | undefined => {
    if (
      updateState.status === 'downloading' ||
      updateState.status === 'ready'
    ) {
      return undefined;
    }
    if (updateState.status === 'available') {
      if (!identity) {
        return undefined;
      }
      const info = updateState.info;
      return () => beginOffer(info);
    }
    if (canCheck) {
      return check;
    }
    if (identity && !identity.capabilities.canCheck) {
      return () => toast(t('settings.update.unsupported'));
    }
    return undefined;
  };
  const open = (url: string | undefined) => {
    if (!url || !openExternal) {
      settingsWindowLogWarn('settings about link unavailable');
      return;
    }
    openExternal(url);
  };

  return (
    <SettingsPane className="items-center">
      <div className="flex w-full flex-col items-center gap-3">
        <button
          type="button"
          aria-label={t('settingsWindow.about.product')}
          className="flex size-[112px] cursor-default items-center justify-center rounded-[32px] border-0 bg-(--app-base-brand) py-0 pr-0 pl-[2.188px]"
          onClick={onLogoTap}
        >
          <img
            src={ABOUT_LOGO}
            alt=""
            width={85.3125}
            height={85.3125}
            className="block"
          />
        </button>
        <div className="flex w-full flex-col items-center gap-1">
          <p className="w-fit pl-3 text-center text-[22px] leading-[26px] font-medium text-(--app-color-text-primary)">
            {t('settingsWindow.about.product')}
          </p>
          <p className="text-center text-[15px] leading-[18px] text-(--app-color-text-tertiary)">
            {t('settingsWindow.about.network')}
          </p>
        </div>
      </div>
      <div className={SETTINGS_CARD}>
        <SettingsNavRow
          icon="i-arrow-up-circle"
          label={t('settingsWindow.about.version')}
          busy={updateState.status === 'checking'}
          onClick={versionClick()}
          trailing={
            <VersionTrailing
              state={updateState}
              version={identity?.version}
              t={t}
              onInstall={() => {
                if (updateService) {
                  runApply('install', () => updateService.install());
                }
              }}
            />
          }
        />
        <SettingsNavRow
          icon="i-wallet-set-network"
          label={t('settingsWindow.about.website')}
          onClick={links.homepage ? () => open(links.homepage) : undefined}
          trailing={
            links.homepage ? (
              <span className="text-[14px] leading-[22px] text-(--app-color-text-link)">
                {siteLabel(links.homepage)}
              </span>
            ) : null
          }
          last
        />
      </div>
      <div className={SETTINGS_CARD}>
        <SettingsNavRow
          icon="i-docs"
          label={t('settingsWindow.about.agreement')}
          onClick={links.terms ? () => open(links.terms) : undefined}
        />
        <SettingsNavRow
          icon="i-security-safe"
          label={t('settingsWindow.about.privacy')}
          onClick={links.privacy ? () => open(links.privacy) : undefined}
          last
        />
      </div>
    </SettingsPane>
  );
}

function VersionTrailing({
  state,
  version,
  t,
  onInstall,
}: {
  state: UpdateState;
  version: string | undefined;
  t: (key: string, args?: Record<string, unknown>) => string;
  onInstall: () => void;
}): ReactNode {
  if (state.status === 'checking') {
    return (
      <span className="text-[16px] leading-[16px] text-(--app-color-text-tertiary)">
        {t('settings.update.checking')}
      </span>
    );
  }
  if (state.status === 'downloading') {
    return (
      <span className="flex items-center text-[14px] leading-[22px] text-(--app-color-text-tertiary)">
        {t('update.prompt.downloadingUnknown')}
        <SettingsGlyph
          id="i-sending1"
          size={24}
          className="size-6 animate-spin"
        />
      </span>
    );
  }
  if (state.status === 'ready') {
    return (
      <button
        type="button"
        className={`${UPDATE_PILL} border-0 bg-(--app-base-orange-a8) text-(--app-color-warning)`}
        onClick={onInstall}
      >
        {t('settingsWindow.about.waitingRestart')}
      </button>
    );
  }
  if (!version) {
    return null;
  }
  if (state.status === 'available') {
    return (
      <span className="flex items-center gap-1.5">
        <span className="text-[16px] leading-[16px] text-(--app-color-text-primary)">
          {`v ${version}`}
        </span>
        <span
          className={`${UPDATE_PILL} bg-(--app-base-green-a8) text-(--app-color-success) dark:bg-(--app-base-green-a10)`}
        >
          {t('settingsWindow.about.updateNew')}
        </span>
      </span>
    );
  }
  return (
    <span className="text-[16px] leading-[16px] text-(--app-color-text-tertiary)">
      {`v ${version}`}
    </span>
  );
}

function updateErrorKey(phase: UpdatePhase): string {
  if (phase === 'download') {
    return 'settings.update.error.download';
  }
  if (phase === 'install') {
    return 'settings.update.error.install';
  }
  return 'settings.update.error';
}

function failureReason(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** http(s) only. A missing or non-http value is a gap, not a stand-in URL. */
function readAboutUrl(key: string, value: unknown): string | undefined {
  if (typeof value !== 'string' || !value.trim()) {
    settingsWindowLogWarn('settings about link missing', { key });
    return undefined;
  }
  try {
    const url = new URL(value);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      settingsWindowLogWarn('settings about link rejected', { key });
      return undefined;
    }
    return url.href;
  } catch {
    settingsWindowLogWarn('settings about link rejected', { key });
    return undefined;
  }
}

function siteLabel(url: string): string {
  const host = new URL(url).hostname;
  return host.startsWith('www.') ? host.slice(4) : host;
}
